// @givmo/org-console — the share kit's transport (CPB-WEB3 subset; Email Campaign plan §2.1).
//
// Every call is a GENERATED op. Two sources, one card:
//
//   a person's kit      GET /users/me/share-kit             (their ONE link; the Board Ask, their page)
//                       GET /users/me/share-kit/qr          (that link in the organization's QR design)
//                       GET /users/me/share-kit/graphics.zip ("Download all")
//   the organization's  GET /org/{org_ref}/fundraisers/{id}/share-kit (the campaign editor's card)
//   kit                 GET /org/{org_ref}/fundraisers/{id}/share/qr  (its canonical link's QR)
//                       GET /org/{org_ref}/fundraisers/{id}/share-kit/graphics.zip ("Download all")
//
// The QR preview is the server's SVG drawn as a `data:` URL in an <img>, as the QR editor's is:
// `img-src` admits `data:` on every console host, and the SVG runs nothing in an <img>.

import type { UseQueryOptions } from '@tanstack/react-query';
import {
  firstErrorCode,
  isGivmoError,
  readBinaryMeta,
  sdk,
  type ShareKitEntityTypeParam,
  type ShareKitOut,
} from '@givmo/api-client';
import { saveQrFile, svgDataUrl } from './org-qr-design-queries';

/** Which kit the card shows. */
export type ShareKitSource =
  | {
      kind: 'person';
      entityType: ShareKitEntityTypeParam;
      entityId: string;
      collateralItemIds: readonly string[];
    }
  | { kind: 'organization'; orgRef: string; fundraiserId: string };

export type ShareKitQrFormat = 'svg' | 'png' | 'pdf';

function personQuery(source: Extract<ShareKitSource, { kind: 'person' }>) {
  return {
    entity_type: source.entityType,
    entity_id: source.entityId,
    collateral_item_ids: [...source.collateralItemIds],
  };
}

/** The server's refusal to hand out a link that could not credit the person. */
export const SHARE_KIT_NO_CREDIT = 'SHARE_KIT_CREDIT_UNAVAILABLE';

/** A read that answered with a status, or the refusal above — no retry. Anything else, one. */
function answeredRetry(failureCount: number, error: Error): boolean {
  const answered =
    isGivmoError(error) &&
    ((error.status !== undefined && error.status < 500) ||
      firstErrorCode(error) === SHARE_KIT_NO_CREDIT);
  return !answered && failureCount < 1;
}

function sourceKey(source: ShareKitSource) {
  return source.kind === 'person'
    ? ([
        'share-kit',
        'person',
        source.entityType,
        source.entityId,
        ...source.collateralItemIds,
      ] as const)
    : (['share-kit', 'organization', source.orgRef, source.fundraiserId] as const);
}

export function shareKitOptions(source: ShareKitSource): UseQueryOptions<ShareKitOut, Error> {
  return {
    queryKey: sourceKey(source),
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      if (source.kind === 'person') {
        const { data } = await sdk.shareKitGetMyShareKit({
          query: personQuery(source),
          signal,
          throwOnError: true,
        });
        return data;
      }
      const { data } = await sdk.orgFundraisersGetOrgFundraiserShareKit({
        path: { org_ref: source.orgRef, fundraiser_id: source.fundraiserId },
        signal,
        throwOnError: true,
      });
      return data;
    },
    retry: answeredRetry,
  };
}

function renderQr(
  source: ShareKitSource,
  format: ShareKitQrFormat,
  parse: { parseAs: 'text' } | { parseAs: 'blob'; responseStyle: 'fields' },
  signal?: AbortSignal,
) {
  return source.kind === 'person'
    ? sdk.shareKitRenderMyShareKitQr({
        query: { ...personQuery(source), format },
        ...parse,
        signal,
      })
    : sdk.orgFundraisersRenderOrgFundraiserShareQr({
        path: { org_ref: source.orgRef, fundraiser_id: source.fundraiserId },
        query: { format },
        ...parse,
        signal,
      });
}

/** The kit's QR, drawn small beside the link. A design renders the same bytes every time. */
export function shareKitQrOptions(source: ShareKitSource): UseQueryOptions<string, Error> {
  return {
    queryKey: [...sourceKey(source), 'qr'],
    queryFn: async ({ signal }: { signal: AbortSignal }) => {
      const result = await renderQr(source, 'svg', { parseAs: 'text' }, signal);
      if (result.error) throw result.error;
      return svgDataUrl(String(result.data ?? ''));
    },
    staleTime: Infinity,
    retry: answeredRetry,
  };
}

function asBlob(data: unknown, type: string): Blob {
  return data instanceof Blob ? data : new Blob([(data ?? '') as BlobPart], { type });
}

/** Download the kit's QR as `format`. Throws on a refusal so the caller can say so. */
export async function downloadShareKitQr(
  source: ShareKitSource,
  format: ShareKitQrFormat,
): Promise<void> {
  const result = await renderQr(source, format, { parseAs: 'blob', responseStyle: 'fields' });
  if (result.error) throw result.error;
  if (!result.response) throw new Error('QR download failed: no response.');
  const filename = readBinaryMeta(result.response).filename ?? `qr-code.${format}`;
  saveQrFile(asBlob(result.data, 'application/octet-stream'), filename);
}

function packGraphics(source: ShareKitSource) {
  const parse = { parseAs: 'blob', responseStyle: 'fields' } as const;
  return source.kind === 'person'
    ? sdk.shareKitDownloadMyShareKitGraphics({ query: personQuery(source), ...parse })
    : sdk.orgFundraisersDownloadOrgFundraiserShareKitGraphics({
        path: { org_ref: source.orgRef, fundraiser_id: source.fundraiserId },
        ...parse,
      });
}

/**
 * Download every graphic the kit shows as one zip ("Download all"). The organization's is its own
 * route, so a staff member's download mints no personal link.
 */
export async function downloadShareKitGraphics(source: ShareKitSource): Promise<void> {
  const result = await packGraphics(source);
  if (result.error) throw result.error;
  if (!result.response) throw new Error('Graphics download failed: no response.');
  const filename = readBinaryMeta(result.response).filename ?? 'graphics.zip';
  saveQrFile(asBlob(result.data, 'application/zip'), filename);
}

/** The kit's email as a `mailto:` her own mail app opens. Givmo sends nothing. */
export function shareKitMailto(email: NonNullable<ShareKitOut['email']>): string {
  return `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
}

/** A link as the card shows it: no scheme. */
export function displayLink(url: string): string {
  return url.replace(/^https?:\/\//, '');
}
